import { useCallback, useEffect, useState } from "react";
import type { CaseItem } from "@/mocks/lawfirm";

export default function CaseModal({
  item,
  onClose,
}: {
  item: CaseItem | null;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState<number | null>(null);

  useEffect(() => {
    setZoom(null);
  }, [item]);

  const closeAll = useCallback(() => {
    setZoom(null);
    onClose();
  }, [onClose]);

  const total = item?.judgments.length ?? 0;

  const goPrev = useCallback(() => {
    setZoom((z) => (z === null || total === 0 ? z : (z - 1 + total) % total));
  }, [total]);

  const goNext = useCallback(() => {
    setZoom((z) => (z === null || total === 0 ? z : (z + 1) % total));
  }, [total]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (zoom !== null) {
          setZoom(null);
          return;
        }
        closeAll();
      }
      if (zoom !== null && total > 1) {
        if (e.key === "ArrowLeft") goPrev();
        if (e.key === "ArrowRight") goNext();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [item, zoom, total, closeAll, goPrev, goNext]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
    >
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={closeAll}
      ></div>

      <div className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white">
        <div className="flex items-start justify-between gap-4 border-b border-background-200 px-5 py-4 md:px-7 md:py-5">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
              {item.category}
            </span>
            <h2 className="mt-3 font-heading text-lg md:text-2xl font-bold text-foreground-950 leading-snug">
              {item.title}
            </h2>
            <p className="mt-1.5 text-xs text-foreground-500">{item.date}</p>
          </div>
          <button
            type="button"
            onClick={closeAll}
            aria-label="닫기"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5 md:px-7 md:py-7">
          <img
            src={item.thumbnail}
            alt={item.title}
            title={`${item.title} 성공사례`}
            className="h-40 w-full rounded-md object-cover object-top md:h-56"
          />

          <div className="mt-6 rounded-md bg-background-50 border border-background-200 p-5">
            <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">사건 개요</h3>
            <div className="mt-3 space-y-3">
              {item.content.map((paragraph, idx) => (
                <p
                  key={idx}
                  className="text-sm md:text-base text-foreground-700 leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3 rounded-md bg-accent-50 px-4 py-3">
              <span className="text-sm text-accent-700">결과</span>
              <span className="text-base md:text-lg font-bold text-accent-600">{item.result}</span>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                판결문
              </h3>
              <span className="text-xs text-foreground-500">
                {item.judgments.length > 1 ? `총 ${item.judgments.length}장 · ` : ""}클릭하면 크게 볼 수 있어요
              </span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {item.judgments.map((src, idx) => (
                <button
                  type="button"
                  key={src}
                  onClick={() => setZoom(idx)}
                  aria-label={`${item.title} 판결문 ${idx + 1} 확대 보기`}
                  className="group relative cursor-pointer overflow-hidden rounded-md border border-background-200 bg-white text-left transition-colors hover:border-accent-400/70"
                >
                  <div className="h-36 w-full overflow-hidden bg-background-100 md:h-44">
                    <img
                      src={src}
                      alt={`${item.title} 판결문 ${idx + 1}`}
                      title={`${item.title} 판결문`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <span className="absolute inset-0 flex items-center justify-center bg-foreground-950/0 opacity-0 transition-all duration-300 group-hover:bg-foreground-950/40 group-hover:opacity-100">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-foreground-900">
                      <i className="ri-zoom-in-line text-lg"></i>
                    </span>
                  </span>
                  {item.judgments.length > 1 && (
                    <span className="absolute bottom-2 right-2 rounded bg-foreground-950/70 px-2 py-0.5 text-[11px] font-medium text-white">
                      {idx + 1} / {item.judgments.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-background-200 px-5 py-4 md:px-7">
          <button
            type="button"
            onClick={closeAll}
            className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 transition-colors sm:w-auto"
          >
            <i className="ri-close-line"></i>
            닫기
          </button>
          <a
            href="https://m.booking.naver.com/booking/6/bizes/1503484/items/7082029?area=pll&lang=ko&startDate=2026-09-21&theme=place"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-accent-600 transition-colors sm:w-auto"
          >
            <i className="ri-calendar-check-line"></i>
            상담 예약
          </a>
        </div>
      </div>

      {zoom !== null && (
        <div
          className="fixed inset-0 z-[110] flex flex-col bg-foreground-950/92"
          role="dialog"
          aria-modal="true"
          aria-label={`${item.title} 판결문 확대`}
        >
          <div className="flex items-center justify-between gap-4 px-4 py-3 md:px-6 md:py-4">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-white">{item.title}</p>
              <p className="mt-0.5 text-xs text-white/60">
                판결문 {zoom + 1} / {item.judgments.length}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setZoom(null)}
              aria-label="확대 닫기"
              className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4 pb-6 md:px-16">
            <img
              src={item.judgments[zoom]}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-30 blur-3xl"
            />
            <div className="pointer-events-none absolute inset-0 bg-foreground-950/40"></div>

            {item.judgments.length > 1 && (
              <button
                type="button"
                onClick={goPrev}
                aria-label="이전 판결문"
                className="absolute left-2 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors md:left-5 md:h-12 md:w-12"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>
            )}

            <img
              src={item.judgments[zoom]}
              alt={`${item.title} 판결문 ${zoom + 1}`}
              title={`${item.title} 판결문`}
              className="relative z-10 max-h-full max-w-full rounded-md object-contain shadow-2xl ring-1 ring-white/25"
            />

            {item.judgments.length > 1 && (
              <button
                type="button"
                onClick={goNext}
                aria-label="다음 판결문"
                className="absolute right-2 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors md:right-5 md:h-12 md:w-12"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}